import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { humanErrorMessage } from '@/api/errors';
import { useClient } from '@/clients/queries';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { PageShell } from '@/components/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDeleteTradeLink, useTradeLink } from '@/trade-links/queries';

const formatDate = (iso: string): string => new Date(iso).toLocaleString('ru-RU');

export const TradeLinkDetailPage = () => {
  const { tradeLinkId } = useParams({ from: '/_protected/trade-links/$tradeLinkId' });
  const navigate = useNavigate();
  const { data: link, isLoading } = useTradeLink(tradeLinkId);
  const { data: client } = useClient(link?.clientId);
  const deleteTradeLink = useDeleteTradeLink();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  if (isLoading) return <div className="p-6 text-muted-foreground">Загрузка…</div>;
  if (!link) return <div className="p-6 text-muted-foreground">Trade-link не найден.</div>;

  const handleDelete = (): void => {
    deleteTradeLink.mutate(link.id, {
      onSuccess: () => {
        toast.success('Trade-link удалён');
        setConfirmDeleteOpen(false);
        void navigate({ to: '/trade-links' });
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const isDeleted = Boolean(link.deletedAt);

  return (
    <div className="space-y-4 p-6">
      <Link
        to="/trade-links"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Trade-links
      </Link>
      <PageShell
        title={link.partnerSteamId}
        description={link.token}
        actions={
          isDeleted ? (
            <Badge variant="destructive">Удалён</Badge>
          ) : (
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmDeleteOpen(true);
              }}
            >
              Удалить
            </Button>
          )
        }
      >
        <dl className="grid grid-cols-[200px_1fr] gap-y-3 p-6 text-sm">
          <dt className="text-muted-foreground">Клиент</dt>
          <dd>
            <Link
              to="/clients/$clientId"
              params={{ clientId: link.clientId }}
              className="hover:underline"
            >
              {client?.name ?? link.clientId}
            </Link>
          </dd>
          <dt className="text-muted-foreground">URL</dt>
          <dd className="break-all font-mono text-xs">{link.rawUrl}</dd>
          <dt className="text-muted-foreground">Источник</dt>
          <dd>
            <Badge variant="secondary">{link.source}</Badge>
          </dd>
          <dt className="text-muted-foreground">Создан</dt>
          <dd>{formatDate(link.createdAt)}</dd>
        </dl>
      </PageShell>

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Удалить trade-link?"
        description={`Trade-link будет soft-deleted. Связанные trade-сессии не отменяются.`}
        onConfirm={handleDelete}
        isPending={deleteTradeLink.isPending}
      />
    </div>
  );
};
